import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import { styled } from 'goober';
import { useTranslation } from 'react-i18next';

import { AiStar } from '@/assets/icons';
import { useExploreAdvancedNavigation } from '@/features/explore/use-explore-advanced-navigation';
import { useExploreProgrammaticSubmit } from '@/features/explore/use-explore-programmatic-submit';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Inline } from '@/ui/line/line';
import { TextArea as TextAreaBase } from '@/ui/text-editable/text-editable.styles';
import { Header } from '@/ui/typography/widgets';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';

const PROMPT_MAX_LENGTH = 1000;

const useHasExploreAccess = (): boolean => {
  const { hasRequiredPermission } = useUserPermissions();
  const { hasRequiredProduct } = useUserProducts();

  return (
    hasRequiredProduct(PRODUCT.CRUNCHBASE) &&
    hasRequiredProduct(PRODUCT.EXPLORE) &&
    hasRequiredPermission(PERMISSION.ADD_COMPANY_TO_STREAM)
  );
};

export const ExploreAiLinkWidget = () => {
  const { t } = useTranslation('homepage', {
    keyPrefix: 'exploreAiLink',
  });
  const hasExploreAccess = useHasExploreAccess();
  const exploreProgrammaticSubmit = useExploreProgrammaticSubmit();
  const exploreAdvancedNavigation = useExploreAdvancedNavigation();
  const [prompt, setPrompt] = useState('');

  const canSubmit = prompt.trim().length > 0;

  const submit = useCallback((): void => {
    exploreProgrammaticSubmit(prompt);
  }, [exploreProgrammaticSubmit, prompt]);

  const openAdvanced = useCallback((): void => {
    exploreAdvancedNavigation(prompt);
  }, [exploreAdvancedNavigation, prompt]);

  const onPromptKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submit();
      }
    },
    [submit],
  );

  if (!hasExploreAccess) {
    return null;
  }

  return (
    <Card data-testid="explore-ai-link-widget" direction="column" gap="16px">
      <Inline alignItems="center" gap="12px" justifyContent="flex-start">
        <AiStarDecoration />
        <Header>{t('headline')}</Header>
      </Inline>
      <InputTrack>
        <PromptArea
          aria-label={t('placeholder')}
          autoComplete="off"
          maxLength={PROMPT_MAX_LENGTH}
          placeholder={t('placeholder')}
          resize="vertical"
          rows={1}
          value={prompt}
          onChange={(e): void => setPrompt(e.target.value)}
          onKeyDown={onPromptKeyDown}
        />
        <Actions alignItems="end" gap="8px">
          <AdvancedButton
            contentPadding="lg"
            height={56}
            startIcon="MagicPen"
            textCenter
            type="button"
            variant="white"
            onClick={() => openAdvanced()}
          >
            {t('advanced')}
          </AdvancedButton>
          <SubmitButton
            aria-label={t('submitAria')}
            disabled={!canSubmit}
            height={56}
            startIcon="Envelope"
            textCenter
            type="button"
            variant="primary"
            width={72}
            onClick={() => submit()}
          />
        </Actions>
      </InputTrack>
    </Card>
  );
};

const AiStarDecoration = styled(AiStar)`
  flex-shrink: 0;
  height: 36px;
  width: 36px;
`;

const Card = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadow.widgets};
  margin: 24px 0;
  padding: 24px;
  transition: box-shadow 0.15s ease;
  width: 100%;

  &:focus-within {
    box-shadow:
      ${({ theme }) => theme.shadow.widgets},
      0 0 0 2px ${({ theme }) => theme.colors.blue.primaryA};
  }
`;

const InputTrack = styled(Flexbox)`
  align-items: end;
  background-color: ${({ theme }) => theme.colors.basics.canvas};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray.c2};
  padding: 8px 8px 8px 12px;
`;

const Actions = styled(Flexbox)`
  flex-shrink: 0;
  margin-left: 3px;
`;

const PromptArea = styled(TextAreaBase)`
  ${({ theme }) => theme.typography.widget.paragraphSmall};
  background-color: transparent;
  flex: 1;
  margin: 0;
  max-height: 220px;
  min-height: 24px;
  min-width: 0;
  padding: 17.5px;

  &::placeholder {
    ${({ theme }) => theme.typography.widget.paragraphSmall};
    color: ${({ theme }) => theme.colors.gray.c12};
    opacity: 1;
  }
`;

const AdvancedButton = styled(Button)`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.blue.primaryA};
  flex-shrink: 0;
  font-size: 18px;
  justify-content: center;
`;

const SubmitButton = styled(Button)`
  border-radius: 10px;
  flex-shrink: 0;
  justify-content: center;
`;
